import { cn } from "~/lib/utils";
import { BadgeVariant, BadgeVariants } from "./types";

// // // //

interface Props {
    label: string | React.ReactNode;
    variant: BadgeVariant;
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    hover?: boolean;
    rounded?: "sm" | "md" | "lg" | "xl" | "full";
    className?: string;
}

export function getBadgeClassName(props: Omit<Props, "label">): string {
    const {
        className,
        size = "sm",
        variant,
        rounded = "sm",
        hover = false,
    } = props;
    return cn("inline-flex items-center font-normal", className, {
        // Size
        // NOTE - it might be good to align this with RowSizes enum
        // ENHANCEMENT - clean up this shitty, shitty logic for props.size -> padding
        "px-1 py-0.5": size === "xs",
        "px-2 py-0.5": size !== "xs" && size !== "xl",
        "px-3 py-0.5": size === "xl",
        "text-[0.625rem]!": size === "xs",
        "text-xs": size === "sm",
        "text-sm": size === "md",
        "text-base": size === "lg",
        "text-lg": size === "xl",
        // Rounded
        "rounded-sm": rounded === "sm",
        rounded: rounded === "md",
        "rounded-lg": rounded === "lg",
        "rounded-xl": rounded === "xl",
        "rounded-full": rounded === "full",
        // Color
        "bg-slate-200 text-slate-800 border": variant === BadgeVariants.gray,
        "bg-slate-300 text-slate-800 border border-slate-400/20":
            variant === BadgeVariants.grayMid,
        "bg-slate-800 text-slate-200 border border-slate-800":
            variant === BadgeVariants.grayDark,
        "bg-red-100 text-red-800 border border-red-200":
            variant === BadgeVariants.red,
        "bg-amber-100 text-amber-800 border border-amber-200":
            variant === BadgeVariants.orange,
        "bg-yellow-100 text-yellow-800 border border-yellow-200":
            variant === BadgeVariants.yellow,
        "bg-green-100 text-green-800 border border-green-200":
            variant === BadgeVariants.green,
        "bg-primary-light text-primary-dark border border-primary-dark/10":
            variant === BadgeVariants.blue,
        "bg-indigo-100 text-indigo-800 border border-indigo-200":
            variant === BadgeVariants.indigo,
        "bg-purple-100 text-purple-900 border border-purple-200":
            variant === BadgeVariants.purple,
        "bg-pink-100 text-pink-800 border border-pink-200":
            variant === BadgeVariants.pink,
        // Color + hover
        "transition-colors": hover,
        "hover:bg-slate-300 hover:border-border":
            hover && variant === BadgeVariants.gray,
        "hover:bg-slate-400 hover:border-slate-400":
            hover && variant === BadgeVariants.grayMid,
        "hover:bg-red-200 hover:border-red-200":
            hover && variant === BadgeVariants.red,
        "hover:bg-amber-200 hover:border-amber-200":
            hover && variant === BadgeVariants.orange,
        "hover:bg-yellow-200 hover:border-yellow-200":
            hover && variant === BadgeVariants.yellow,
        "hover:bg-green-200 hover:border-green-200":
            hover && variant === BadgeVariants.green,
        "hover:bg-primary-dark hover:text-primary-light hover:border-primary-dark":
            hover && variant === BadgeVariants.blue,
        "hover:bg-indigo-200 hover:border-indigo-200":
            hover && variant === BadgeVariants.indigo,
        "hover:bg-purple-200 hover:border-purple-200":
            hover && variant === BadgeVariants.purple,
        "hover:bg-pink-200 hover:border-pink-200":
            hover && variant === BadgeVariants.pink,
        // Outline
        "border border-gray-800 text-slate-800":
            variant === BadgeVariants.grayOutline,
        "border border-red-800 text-red-800":
            variant === BadgeVariants.redOutline,
        "border border-yellow-800 text-yellow-800":
            variant === BadgeVariants.yellowOutline,
        "border border-green-800 text-green-800":
            variant === BadgeVariants.greenOutline,
        "border border-blue-800 text-blue-800":
            variant === BadgeVariants.blueOutline,
        "border border-indigo-800 text-indigo-800":
            variant === BadgeVariants.indigoOutline,
        "border border-purple-800 text-purple-800":
            variant === BadgeVariants.purpleOutline,
        "border border-pink-800 text-pink-800":
            variant === BadgeVariants.pinkOutline,
        // Outline + hover
        "hover:bg-gray-800 hover:text-slate-100":
            hover && variant === BadgeVariants.grayOutline,
        "hover:bg-red-800 hover:text-red-100":
            hover && variant === BadgeVariants.redOutline,
        "hover:bg-yellow-800 hover:text-yellow-100":
            hover && variant === BadgeVariants.yellowOutline,
        "hover:bg-green-800 hover:text-green-100":
            hover && variant === BadgeVariants.greenOutline,
        "hover:bg-blue-800 hover:text-blue-100":
            hover && variant === BadgeVariants.blueOutline,
        "hover:bg-indigo-800 hover:text-indigo-100":
            hover && variant === BadgeVariants.indigoOutline,
        "hover:bg-purple-800 hover:text-purple-100":
            hover && variant === BadgeVariants.purpleOutline,
        "hover:bg-pink-800 hover:text-pink-100":
            hover && variant === BadgeVariants.pinkOutline,
    });
}

export function Badge(props: Props) {
    const { label, variant, className, size, rounded, hover } = props;

    return (
        <span
            className={getBadgeClassName({
                variant,
                className,
                size,
                rounded,
                hover,
            })}
        >
            {label}
        </span>
    );
}
